// Invoices page — list of invoice records (one per guard per job).
// Owner can filter by status, pay cycle, and broker, mark invoices as
// sent/paid/void, and re-download the workbook + POD package.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import {
  watchInvoices, setInvoiceStatus, payCycleFor, payCycleLabel,
  setGuardPaid, clearGuardPaid, markInvoicesSent,
  dispatcherCycleFor, shiftDispatcherCycle, markInvoicesPaid,
  inferInvoiceDispatcher, backfillInvoiceScheduledDates,
  backfillFlatFeeInvoiceTotals, backfillInvoicePackageDetails,
  backfillPayCycleFirstOfMonth,
  updateInvoice,
} from "../../services/invoices";
import { getJob, getBrokers, updateJob } from "../../services/firestore";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { downloadInvoiceWorkbook } from "../../services/invoiceWorkbook";
import { downloadPodPdf } from "../../services/podPdf";
import { downloadMonthlyBrokerWorkbook } from "../../services/monthlyFerrariWorkbook";
import JobDetailModal from "../../components/owner/JobDetailModal";
import "./OwnerLayout.css";
import { invoiceLabel } from "../../services/invoiceLabel";

function formatMoney(n) {
  const v = Number(n) || 0;
  return `$${v.toFixed(2)}`;
}

// Billed hours for a row. Flat-rate jobs (DAI) record 0 hours because the
// fee is fixed, so show a dash — "0" reads like nobody was on the clock.
// Trailing zeros go: 8.00 -> "8", 7.50 -> "7.5", 7.68 -> "7.68".
function formatHours(n) {
  const v = Number(n) || 0;
  if (v <= 0) return "—";
  return v.toFixed(2).replace(/\.?0+$/, "");
}

function formatDate(ts) {
  if (!ts) return "";
  let ms = null;
  if (typeof ts === "number") ms = ts;
  else if (typeof ts === "string") ms = Date.parse(ts);
  else if (ts.toMillis) ms = ts.toMillis();
  else if (ts.seconds != null) ms = ts.seconds * 1000;
  if (ms == null || Number.isNaN(ms)) return "";
  const d = new Date(ms);
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}

// Column tracks for the per-guard and grouped-by-job lists. Every row is
// its own grid, so the flexible Broker column gets a fixed floor: with a
// bare 1fr it shrank to each row's own text width once the fixed columns
// filled the panel, and rows stopped lining up with the header. Below the
// min width the panel scrolls sideways instead of squeezing.
const PERGUARD_COLS = "30px 120px 110px minmax(170px, 1fr) 80px 70px 56px 56px 90px 56px 80px 220px";
const PERGUARD_MIN_WIDTH = 1300;
const GROUPED_COLS = "120px 110px minmax(170px, 1fr) 80px 70px 56px 56px 90px 56px 80px 200px";
const GROUPED_MIN_WIDTH = 1220;

const STATUS_LABELS = {
  draft: { label: "Draft", color: "#666", bg: "#f0f0f0" },
  sent: { label: "Sent", color: "#185FA5", bg: "#E6F0FA" },
  paid: { label: "Paid", color: "#0E7C46", bg: "#E0F4E8" },
  void: { label: "Void", color: "#a32d2d", bg: "#FBE7E7" },
};

export default function Invoices() {
  const { userProfile } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [brokerFilter, setBrokerFilter] = useState("all");
  // Monthly Ferrari/Executive bill controls. Defaults to the current
  // calendar month so the most common case is one click to download.
  const todayYm = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const [monthlyBroker, setMonthlyBroker] = useState("Ferrari");
  const [monthlyYm, setMonthlyYm] = useState(todayYm);
  const [monthlyBusy, setMonthlyBusy] = useState(false);
  // Jobs for the selected broker + month that never went through Ready to
  // Bill. They're absent from the workbook with no error, so they get
  // their own warning above the Download button.
  const [monthlyUnbilled, setMonthlyUnbilled] = useState([]);
  const [showUnbilled, setShowUnbilled] = useState(false);
  // Toggle for the unpaid-guard filter banner. Declared up here, before
  // the filtered useMemo that depends on it (otherwise it throws a
  // temporal-dead-zone error on first render).
  const [showOnlyUnpaidGuard, setShowOnlyUnpaidGuard] = useState(false);
  // Selected invoice ids for bulk operations (mark sent / mark paid).
  // Set via the checkboxes on the invoice list rows.
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Phone width: statements and per-guard lines as cards, shorter banners.
  const [isPhone, setIsPhone] = useState(typeof window !== "undefined" && window.innerWidth < 700);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Job to preview in the read-only JobDetailModal. Opened by clicking
  // an invoice number — answers "which job is this invoice for?" which
  // is non-obvious for brokers like Ferrari that don't issue a job#.
  const [viewJobId, setViewJobId] = useState(null);
  // Aggregate-by-job toggle. When on, all of the current job's per-guard
  // invoices collapse into a single row showing the summed broker total.
  // Defaults ON — the owner-facing question is almost always "what does
  // the broker owe me?"; untoggle for guard-payroll detail.
  const [groupByJob, setGroupByJob] = useState(true);
  // Free-text search across invoice #, job # / HAWB, broker, and guard —
  // pasting a Ferrari order number finds its invoice instantly.
  // A ?q= link (the job screen's "Open in Invoices") pre-fills the search
  // and opens the per-guard list, where the search applies.
  const [searchParams] = useSearchParams();
  const linkedQuery = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(linkedQuery);
  // Top-level view mode. "statements" (default) shows one row per
  // payer+period — the check you collect. "perguard" drops to the
  // granular invoice list (with its own Group-by-job sub-toggle) for
  // payroll work.
  const [viewMode, setViewMode] = useState(linkedQuery ? "perguard" : "statements");
  // Following a new link while already on this page (job modal opened
  // from an invoice row) — the state above only reads the URL on mount.
  useEffect(() => {
    if (!linkedQuery) return;
    setSearchQuery(linkedQuery);
    setViewMode("perguard");
  }, [linkedQuery]);

  // Every job for the selected broker + month, split into the ones that
  // have been through Ready to Bill and the ones that haven't. The
  // unbilled ones are the point: they're silently missing from the
  // workbook, because the monthly build only takes jobs with a
  // billingBreakdown. Shared by the download and the warning banner so
  // the two can never disagree about which jobs are in scope.
  const loadMonthlyJobSplit = useCallback(async () => {
    const snap = await getDocs(query(
      collection(db, "jobs"),
      where("companyId", "==", userProfile.companyId),
    ));
    const target = monthlyBroker.toLowerCase();
    const dispatcherFor = (j) => {
      const bn = String(j.brokerName || "").toLowerCase();
      const b = bn
        ? (brokers.find((x) => (x.name || "").toLowerCase() === bn)
           || brokers.find((x) => {
                const xn = (x.name || "").toLowerCase();
                return xn && (bn.includes(xn) || xn.includes(bn));
              }))
        : null;
      return String(b?.dispatcherName || j.dispatcherName || "").toLowerCase();
    };
    const inScope = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((j) =>
        String(j.billingCycleDate || j.scheduledDate || "").startsWith(monthlyYm)
        && (String(j.brokerName || "").toLowerCase().includes(target)
            || dispatcherFor(j).includes(target)));
    return {
      billed: inScope.filter((j) => j.billingBreakdown),
      // A cancelled trip was never run and bills nothing, so it isn't
      // missing from the bill — leave it out of the warning.
      unbilled: inScope.filter((j) => !j.billingBreakdown && j.status !== "cancelled"),
    };
  }, [userProfile?.companyId, monthlyBroker, monthlyYm, brokers]);

  // Check for unbilled jobs whenever the broker or month changes, so the
  // warning is already on screen before the owner hits Download.
  useEffect(() => {
    if (!userProfile?.companyId) return;
    let cancelled = false;
    loadMonthlyJobSplit()
      .then(({ unbilled }) => { if (!cancelled) setMonthlyUnbilled(unbilled); })
      .catch((err) => { console.warn("Couldn't check for unbilled monthly jobs", err); });
    return () => { cancelled = true; };
  }, [loadMonthlyJobSplit, userProfile?.companyId]);

  async function handleDownloadMonthly() {
    if (!userProfile?.companyId) return;
    setMonthlyBusy(true);
    try {
      // Only jobs that have been through Ready to Bill make the workbook —
      // see loadMonthlyJobSplit, which also feeds the unbilled warning.
      const { billed: monthJobs, unbilled } = await loadMonthlyJobSplit();
      setMonthlyUnbilled(unbilled);
      if (monthJobs.length === 0) {
        flashToast(`No billed ${monthlyBroker} jobs found for ${monthlyYm}.`);
        setMonthlyBusy(false);
        return;
      }
      const result = await downloadMonthlyBrokerWorkbook(monthlyBroker, monthlyYm, monthJobs);
      // Surface the receipts outcome — Ferrari requires them with the
      // bill, so a failed receipt download needs to be loud, not silent.
      const receiptsNote = result.receiptsIncluded > 0
        ? `, ${result.receiptsIncluded} receipt PDF${result.receiptsIncluded === 1 ? "" : "s"} downloaded`
        : "";
      const failNote = result.receiptsFailed > 0
        ? ` ⚠ ${result.receiptsFailed} receipt${result.receiptsFailed === 1 ? "" : "s"} couldn't load — ${result.receiptsFailReason || "check that expense's receipt in the job's Review"}.`
        : "";
      flashToast(`${result.filename} downloaded — ${result.jobsIncluded} job${result.jobsIncluded === 1 ? "" : "s"}, $${result.grandTotal.toFixed(2)} total${receiptsNote}.${failNote}`);
    } catch (err) {
      console.error("Monthly bill download failed", err);
      flashToast("Couldn't generate monthly bill: " + (err?.message || err));
    }
    setMonthlyBusy(false);
  }
  // Brief feedback message for actions (status flip, guard pay, bundle send)
  const [toast, setToast] = useState("");
  function flashToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  }

  useEffect(() => {
    if (!userProfile?.companyId) return;
    getBrokers(userProfile.companyId).then(setBrokers).catch(console.error);
    const unsub = watchInvoices(userProfile.companyId, (list) => {
      // billBroker=false invoices (e.g. a cancelled trip we pay the guard for
      // but don't charge the broker) are guard-pay-only — keep them out of the
      // broker statements. Default (undefined) bills, so normal jobs are unaffected.
      setInvoices(list.filter((i) => i.billBroker !== false));
      setLoading(false);
    });
    // Self-heal old invoices that don't have scheduledDate yet — looks
    // up their underlying job and copies the date over. Runs once per
    // page load; idempotent (skips invoices that already have it).
    // Non-blocking; the snapshot listener picks up the writes naturally.
    backfillInvoiceScheduledDates(userProfile.companyId)
      .then((n) => {
        if (n > 0) {
          // Console log only — don't toast on every load. The
          // listener will refresh the data automatically.
          console.log(`Backfilled scheduledDate on ${n} invoice(s).`);
        }
      })
      .catch((err) => console.warn("Invoice backfill failed", err));
    // One-time repair of stale $0 flat-fee invoices (pre flat-fee-aware
    // createInvoicesForJob). Idempotent — only touches total===0
    // draft/sent invoices whose job has a real flat fee. Toast when it
    // actually fixes something so the owner knows the numbers changed.
    backfillFlatFeeInvoiceTotals(userProfile.companyId)
      .then((n) => {
        if (n > 0) {
          flashToast(`Corrected ${n} flat-fee invoice${n === 1 ? "" : "s"} that were showing $0.`);
        }
      })
      .catch((err) => console.warn("Flat-fee invoice backfill failed", err));
    // Self-heal old invoices missing packageDetails (Order/Receipt #s +
    // package addresses, shown in the Statements detail rows). Same
    // once-per-load idempotent pattern as the scheduledDate backfill.
    backfillInvoicePackageDetails(userProfile.companyId)
      .then((n) => {
        if (n > 0) console.log(`Backfilled packageDetails on ${n} invoice(s).`);
      })
      .catch((err) => console.warn("Package-details backfill failed", err));
    // One-time repair: invoices whose job completed on the 1st of a month
    // were bucketed into the wrong pay cycle (that month's "2–15" instead
    // of the closing "16th–1st" half). Only touches not-yet-sent invoices;
    // toast so the owner knows a cycle total moved.
    backfillPayCycleFirstOfMonth(userProfile.companyId)
      .then((n) => {
        if (n > 0) {
          flashToast(`Moved ${n} first-of-month invoice${n === 1 ? "" : "s"} to the correct pay cycle.`);
        }
      })
      .catch((err) => console.warn("Pay-cycle backfill failed", err));
    return unsub;
  }, [userProfile?.companyId]);

  // Derived filter options
  const cycleOptions = useMemo(() => {
    const set = new Set(invoices.map((i) => i.payCycle).filter(Boolean));
    // Sort newest first (cycle ID is YYYY-MM-X — string sort works).
    return Array.from(set).sort().reverse();
  }, [invoices]);

  // Apply filters, then sort by pay cycle (newest first) and, within a
  // cycle, by the DATE the table displays so the column reads in order.
  const filtered = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    const filteredList = invoices.filter((inv) => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (cycleFilter !== "all" && inv.payCycle !== cycleFilter) return false;
      if (brokerFilter !== "all" && inv.brokerName !== brokerFilter) return false;
      // Guard-pay-only mode (banner toggle) — narrow to invoices the
      // owner hasn't yet recorded a guard payment for.
      if (showOnlyUnpaidGuard && (inv.guardPaidAmount != null || inv.status === "void")) return false;
      if (trimmedQuery) {
        const hay = [
          inv.invoiceNumber, inv.jobNumber, invoiceLabel(inv), inv.brokerName, inv.guardName,
          ...(inv.packageDetails || []).map((p) => p.number),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(trimmedQuery)) return false;
      }
      return true;
    });
    const ms = (ts) => {
      if (!ts) return Infinity;
      if (typeof ts === "number") return ts;
      if (typeof ts === "string") return Date.parse(ts) || Infinity;
      if (ts.toMillis) return ts.toMillis();
      if (ts.seconds != null) return ts.seconds * 1000;
      return Infinity;
    };
    // The same date the DATE column renders: scheduledDate first, then
    // completedAt/createdAt. Sorting on the displayed value keeps the
    // column monotonic — that's what makes an invoice findable by eye.
    const dayKey = (i) => {
      if (i.scheduledDate) return i.scheduledDate;
      const t = ms(i.completedAt || i.createdAt);
      return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 10) : "9999-99-99";
    };
    return filteredList.slice().sort((a, b) => {
      // Cycle DESC (newest first)
      const ca = a.payCycle || "";
      const cb = b.payCycle || "";
      if (ca !== cb) return cb.localeCompare(ca);
      const da = dayKey(a), db = dayKey(b);
      if (da !== db) return da.localeCompare(db);
      // Same day: invoice # keeps the order stable.
      return String(a.invoiceNumber || "").localeCompare(String(b.invoiceNumber || ""));
    });
  }, [invoices, statusFilter, cycleFilter, brokerFilter, showOnlyUnpaidGuard, searchQuery]);

  // Per-status counts (drives the chip-bar badges)
  const counts = useMemo(() => {
    const c = { all: invoices.length, draft: 0, sent: 0, paid: 0, void: 0 };
    invoices.forEach((i) => { if (c[i.status] != null) c[i.status]++; });
    return c;
  }, [invoices]);

  // Guard-pay tracker: how many invoices in the current filter still
  // need a guard payment recorded. Drives the amber summary banner so
  // the owner can never silently miss an entry.
  const guardUnpaidCount = useMemo(() => {
    return filtered.filter((i) =>
      i.guardPaidAmount == null && i.status !== "void"
    ).length;
  }, [filtered]);

  // ── Statements ──────────────────────────────────────────────────────
  // The payer-centric view: group every (non-void) invoice into a
  // Statement = one payer (dispatcher) + one billing period = the single
  // check you collect. Derived, not stored — per-guard invoices remain
  // the source of truth; a Statement just rolls them up. Payment status
  // is derived from the underlying invoices and cascades back to them on
  // Mark sent / Mark paid.
  const statements = useMemo(() => {
    const toMs = (v) => (
      v?.toMillis?.() ??
      (typeof v === "number" ? v :
       v instanceof Date ? v.getTime() :
       Date.parse(v || ""))
    ) || 0;
    const refMsOf = (inv) => {
      // billingCycleDate is the reconciliation override: when the owner
      // moves a job to another statement (check didn't cover it), we
      // stamp the target cycle's start date here. It wins over the
      // actual scheduledDate for grouping ONLY — the job's real date is
      // never rewritten.
      const dateStr = inv.billingCycleDate || inv.scheduledDate;
      if (dateStr) {
        const [y, mo, d] = String(dateStr).split("-").map(Number);
        if (y && mo && d) return new Date(y, mo - 1, d, 12, 0).getTime();
      }
      return toMs(inv.completedAt) || toMs(inv.createdAt) || 0;
    };
    const byKey = new Map();
    for (const inv of invoices) {
      if (inv.status === "void") continue; // void doesn't belong to a bill
      const payer = inferInvoiceDispatcher(inv); // Orion / Ferrari / Executive
      const refMs = refMsOf(inv);
      if (!refMs) continue;
      const cycle = dispatcherCycleFor(payer, new Date(refMs));
      const key = cycle.id; // e.g. "Orion-2026-05-B" / "Ferrari-2026-05"
      const cur = byKey.get(key) || { key, payer, cycle, invoices: [] };
      cur.invoices.push(inv);
      byKey.set(key, cur);
    }
    // Statement status from its invoices: all paid → paid; any draft →
    // draft; otherwise (some sent, none draft) → sent.
    const deriveStatus = (invs) => {
      if (invs.length && invs.every((i) => i.status === "paid")) return "paid";
      if (invs.some((i) => i.status === "draft")) return "draft";
      if (invs.some((i) => i.status === "sent")) return "sent";
      return "draft";
    };
    const out = [];
    for (const s of byKey.values()) {
      const total = s.invoices.reduce((sum, i) => sum + (Number(i.total) || 0), 0);
      const status = deriveStatus(s.invoices);
      // Roll up to jobs for the expand view (hide per-guard by default).
      const jobMap = new Map();
      for (const inv of s.invoices) {
        const jk = inv.jobId || `solo:${inv.id}`;
        const jc = jobMap.get(jk) || {
          jobId: inv.jobId || null,
          jobNumber: inv.jobNumber || "",
          brokerName: inv.brokerName || "",
          jobType: inv.jobType || "",
          routeSummary: inv.routeSummary || "",
          scheduledDate: inv.scheduledDate || "",
          completedAt: inv.completedAt || inv.createdAt || null,
          invoices: [],
          total: 0,
          hours: 0,
          miles: 0,
          packageDetails: [],
          moved: false,
        };
        jc.invoices.push(inv);
        // Flag jobs living in a statement other than their natural one,
        // so the row can show a "moved" marker during reconciliation.
        if (inv.billingCycleDate) jc.moved = true;
        jc.total += Number(inv.total) || 0;
        // Per-guard invoices share the job's hours/miles (miles live on
        // the driver's invoice only) — max gives the job-level numbers
        // without double-counting across guards.
        jc.hours = Math.max(jc.hours, Number(inv.hoursCharged) || 0);
        jc.miles = Math.max(jc.miles, Number(inv.miles) || 0);
        if (!jc.packageDetails.length && Array.isArray(inv.packageDetails)) {
          jc.packageDetails = inv.packageDetails;
        }
        jobMap.set(jk, jc);
      }
      const jobs = [...jobMap.values()].sort((a, b) =>
        String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || ""))
      );
      // Payment ref/date — pull from any paid invoice in the statement
      // (markInvoicesPaid stamps the same ref across the batch).
      const paidRef = s.invoices.find((i) => i.paymentReference)?.paymentReference || null;
      const paidAt = s.invoices.find((i) => i.paymentReceivedAt)?.paymentReceivedAt || null;
      out.push({
        ...s,
        total,
        status,
        jobs,
        jobCount: jobMap.size,
        invoiceCount: s.invoices.length,
        paidRef,
        paidAt,
      });
    }
    // Newest period first; ties broken by payer name.
    out.sort((a, b) => (b.cycle.startMs - a.cycle.startMs) || a.payer.localeCompare(b.payer));
    return out;
  }, [invoices]);

  // Status-chip filter applies to statement-level status in this view.
  const visibleStatements = useMemo(
    () => statements.filter((s) => statusFilter === "all" || s.status === statusFilter),
    [statements, statusFilter]
  );

  // Bulk-mark-paid dialog. Dispatched from a Statement's Mark paid
  // action or the per-guard list multi-select. The form is the same.
  const [paymentDialog, setPaymentDialog] = useState(null);
  // paymentDialog shape: { invoiceIds, title, defaultAmount } or null.

  async function handleMarkSelectedPaid() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const selectedInvoices = invoices.filter((i) => selectedIds.has(i.id) && i.status !== "paid");
    if (selectedInvoices.length === 0) {
      flashToast("All selected invoices are already paid.");
      return;
    }
    setPaymentDialog({
      invoiceIds: selectedInvoices.map((i) => i.id),
      title: `Mark ${selectedInvoices.length} selected invoice${selectedInvoices.length === 1 ? "" : "s"} paid`,
      defaultAmount: selectedInvoices.reduce((s, i) => s + (Number(i.total) || 0), 0),
    });
  }

  // Move jobs to another statement period (check reconciliation).
  // Stamps billingCycleDate = target cycle's start date on every
  // invoice of every selected job (drives the Statements grouping) AND
  // on the job doc itself (drives the Ferrari/Executive monthly bill's
  // month filter), so both views agree. The job's real scheduledDate is
  // never modified. Moving back = moving to the original period.
  async function handleMoveJobs(jobClusters, targetCycle) {
    const d = new Date(targetCycle.startMs);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    try {
      for (const jc of jobClusters) {
        for (const inv of jc.invoices) {
          await updateInvoice(inv.id, { billingCycleDate: dateStr });
        }
        if (jc.jobId) await updateJob(jc.jobId, { billingCycleDate: dateStr });
      }
      flashToast(`Moved ${jobClusters.length} job${jobClusters.length === 1 ? "" : "s"} to ${targetCycle.label}`);
    } catch (err) {
      console.error("Move to another statement failed", err);
      flashToast("Couldn't move: " + (err?.message || err));
    }
  }

  // Totals in current filter view: what the broker pays vs what the owner
  // has paid the guards. The gap is the owner's margin.
  const filteredTotal = useMemo(() => {
    return filtered.reduce((sum, i) => sum + (Number(i.total) || 0), 0);
  }, [filtered]);
  const filteredPaidOut = useMemo(() => {
    return filtered.reduce((sum, i) => sum + (Number(i.guardPaidAmount) || 0), 0);
  }, [filtered]);

  async function handleStatus(inv, next) {
    try {
      await setInvoiceStatus(inv.id, next);
      const verbs = { sent: "marked sent", paid: "marked paid", void: "voided", draft: "restored" };
      flashToast(`${inv.invoiceNumber} ${verbs[next] || "updated"}`);
    } catch (err) {
      alert("Update failed: " + (err?.message || err));
    }
  }

  // Prompt the owner for the amount paid to the guard for this invoice and
  // save it. Pre-fills with the invoice total as a starting suggestion.
  async function handleGuardPay(inv) {
    const suggested = inv.guardPaidAmount != null
      ? inv.guardPaidAmount
      : (Number(inv.total) || 0);
    const raw = window.prompt(
      `Record payment to ${inv.guardName || `Guard ${inv.guardNumber}`}\n` +
      `Invoice ${inv.invoiceNumber} · Broker bills ${formatMoney(inv.total)}\n\n` +
      `Amount paid to guard:`,
      String(suggested)
    );
    if (raw == null) return;
    const amount = Number(String(raw).replace(/[^\d.]/g, ""));
    if (!Number.isFinite(amount) || amount < 0) {
      return alert("Invalid amount.");
    }
    try {
      await setGuardPaid(inv.id, amount);
      // Optimistic local update — flips the button to green immediately.
      // The watchInvoices snapshot will sync the canonical state shortly
      // after, but this avoids a perceptible delay or stale display.
      setInvoices((prev) => prev.map((i) =>
        i.id === inv.id
          ? { ...i, guardPaidAmount: amount, guardPaidAt: new Date() }
          : i
      ));
      flashToast(`Recorded ${formatMoney(amount)} paid to ${inv.guardName || "guard"}`);
    } catch (err) {
      alert("Failed to save: " + (err?.message || err));
    }
  }

  async function handleClearGuardPay(inv) {
    if (!window.confirm(`Clear guard payment record on ${inv.invoiceNumber}?`)) return;
    try {
      await clearGuardPaid(inv.id);
      // Same optimistic flip back to "needs pay" so the UI reacts now.
      setInvoices((prev) => prev.map((i) =>
        i.id === inv.id
          ? { ...i, guardPaidAmount: null, guardPaidAt: null }
          : i
      ));
      flashToast(`Cleared payment record on ${inv.invoiceNumber}`);
    } catch (err) { alert("Failed: " + (err?.message || err)); }
  }

  // Compose and open a mailto for the whole bundle, then mark every invoice
  // in the bundle as Sent. The user still has to forward the original
  // schedule email manually (browsers can't attach external email).
  async function handleBundleSend(bundle) {
    if (bundle.invoices.length === 0) return;
    const lines = [];
    lines.push("I completed the following jobs:");
    lines.push("");
    // Fetch each job for date + route. Best-effort; use invoice fields if
    // the job has been deleted.
    for (const inv of bundle.invoices) {
      let routeStr = "";
      try {
        const job = inv.jobId ? await getJob(inv.jobId) : null;
        const stops = (job?.stops || []).filter((s) => s.type !== "airport");
        if (stops.length >= 2) {
          const labelOf = (s) => s.outOfTown ? "OUT OF TOWN" : (s.locationName || "—");
          routeStr = `${labelOf(stops[0])} → ${labelOf(stops[stops.length - 1])}`;
        } else if (stops.length === 1) {
          routeStr = stops[0].locationName || "";
        }
      } catch (_) { /* ignore */ }
      const dateStr = formatDate(inv.completedAt) || "—";
      lines.push(`• ${dateStr} — ${routeStr || inv.jobNumber || ""} (${inv.brokerName}${inv.guardName ? `, ${inv.guardName}` : ""})`);
    }
    lines.push("");
    lines.push(`Total: ${bundle.invoices.length} job${bundle.invoices.length === 1 ? "" : "s"} = ${formatMoney(bundle.totalAmount)}`);
    lines.push("");
    lines.push(`Name: ${userProfile?.name || ""}`);
    lines.push("Contact: (your contact #)");
    lines.push("");
    lines.push("The original schedule email is forwarded with this submission.");
    const subject = `Invoice — ${bundle.brokerName} — ${bundle.salesRepName || "schedule"} — ${bundle.invoices.length} job${bundle.invoices.length === 1 ? "" : "s"}`;
    const href = `mailto:accountspayable@orionsecurityservices.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
    // Mark them all sent first, then open the mail composer. If the user
    // cancels in their mail client they can flip status back via the row's
    // ✕ → ↺ controls.
    try {
      await markInvoicesSent(bundle.invoices.map((i) => i.id));
      flashToast(`${bundle.invoices.length} invoices marked sent — opening email…`);
    } catch (err) {
      alert("Couldn't mark invoices sent: " + (err?.message || err));
      return;
    }
    window.location.href = href;
  }

  async function handleDownload(inv) {
    try {
      const job = await getJob(inv.jobId);
      if (!job) return alert("Original job not found.");
      const broker = brokers.find((b) => b.id === inv.brokerId) || { name: inv.brokerName };
      await downloadInvoiceWorkbook(job, broker);
      await downloadPodPdf(job);
    } catch (err) {
      alert("Download failed: " + (err?.message || err));
      console.error(err);
    }
  }

  return (
    <div>
      {/* Bottom-center toast — fades out after 3.5s. */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#0E7C46", color: "#fff", padding: "10px 18px",
          borderRadius: 8, fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 1000,
        }}>
          ✓ {toast}
        </div>
      )}

      <div className="page-header">
        <div>
          <div className="page-title">Invoices</div>
          <div className="page-subtitle">
            {loading
              ? "Loading..."
              : `${filtered.length !== invoices.length
                    ? `${filtered.length} of ${invoices.length}`
                    : `${invoices.length} total`} · ${formatMoney(filteredTotal)} billed${
                  filteredPaidOut > 0
                    ? ` · ${formatMoney(filteredPaidOut)} paid to guards · ${formatMoney(filteredTotal - filteredPaidOut)} margin`
                    : ""
                }`}
          </div>
        </div>
      </div>

      <div className="page-body">
        {/* Cycle summary card retired — the Statements view below now
            shows per-payer, per-cycle totals and payment status as its
            primary list, so the old summary panel was redundant. */}

        {/* Monthly Ferrari/Executive bill — these brokers invoice as a
            single batch at month end (one xlsx with one row per package
            + expenses listed under each job). The dropdown picks broker
            + month; the download button generates the file from every
            billed job matching those criteria. Receipts go to email
            separately as attachments — not bundled into the workbook. */}
        <div style={{
          background: "#fffbe9", border: "1px solid #f0d878",
          borderRadius: 8, padding: "10px 14px", marginBottom: 14,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 18 }}>📑</span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#7a5400" }}>Monthly bill</div>
            {!isPhone && (
            <div style={{ fontSize: 10, color: "#a36b1d" }}>
              Ferrari & Executive invoice as a single monthly batch.
              One row per package, expenses under each job, grand total at the bottom.
            </div>
            )}
          </div>
          <select
            value={monthlyBroker}
            onChange={(e) => setMonthlyBroker(e.target.value)}
            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #d0c890", fontSize: 12 }}
          >
            <option value="Ferrari">Ferrari</option>
            <option value="Executive">Executive</option>
          </select>
          <input
            type="month"
            value={monthlyYm}
            onChange={(e) => setMonthlyYm(e.target.value)}
            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #d0c890", fontSize: 12 }}
          />
          <button
            className="btn btn-blue btn-sm"
            onClick={handleDownloadMonthly}
            disabled={monthlyBusy}
          >
            {monthlyBusy ? "Generating..." : `⬇ Download ${monthlyBroker} ${monthlyYm}`}
          </button>
        </div>

        {/* Unbilled-jobs warning. The workbook silently takes only jobs
            with a billingBreakdown, so a job that was run but never
            opened in Ready to Bill just isn't on the bill — no error,
            no row, no money. Name them before the download, not after. */}
        {monthlyUnbilled.length > 0 && (
          <div style={{
            background: "#FFF4F4", border: "1px solid #E7A9A9",
            borderLeft: "4px solid #C04A4A", borderRadius: 8,
            padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#8A2F2F",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 16 }}>⚠</span>
              <div style={{ flex: 1, minWidth: isPhone ? 0 : 240 }}>
                {isPhone ? (
                  <><strong>{monthlyUnbilled.length} {monthlyBroker} job{monthlyUnbilled.length === 1 ? "" : "s"}</strong> ({monthlyYm}) not billed yet</>
                ) : (<>
                <strong>{monthlyUnbilled.length} {monthlyBroker} job{monthlyUnbilled.length === 1 ? "" : "s"} in {monthlyYm} {monthlyUnbilled.length === 1 ? "has" : "have"} no billing record</strong>
                {" — "}they won't be on this bill. Open each in Ready to Bill and save or approve it first.
                </>)}
              </div>
              <button className="btn btn-sm" onClick={() => setShowUnbilled((v) => !v)}>
                {showUnbilled ? "Hide" : "Show"}
              </button>
            </div>
            {showUnbilled && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
                {monthlyUnbilled
                  .slice()
                  .sort((a, b) => String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || "")))
                  .map((j) => (
                    <button
                      key={j.id}
                      type="button"
                      onClick={() => setViewJobId(j.id)}
                      style={{
                        background: "none", border: "none", padding: 0, cursor: "pointer",
                        textAlign: "left", fontSize: 11, color: "#8A2F2F",
                      }}
                      title="Open the job"
                    >
                      {j.scheduledDate || "no date"}
                      {" · "}
                      <span style={{ fontFamily: "monospace", textDecoration: "underline" }}>
                        {j.jobNumber || j.packages?.[0]?.hawbNumber || j.id}
                      </span>
                      {" · "}{j.brokerName || "—"}
                      {" · "}{j.status}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Guard-pay tracker — amber banner counts how many invoices in
            the current view still need a guard payment recorded.
            Click the button to filter the list to ONLY those rows so
            you can knock them out one by one. Hides when zero. */}
        {(guardUnpaidCount > 0 || showOnlyUnpaidGuard) && (
          <div style={{
            background: "#fffbf2", border: "1px solid #EF9F27",
            borderLeft: "4px solid #EF9F27", borderRadius: 8,
            padding: "10px 14px", marginBottom: 12,
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          }}>
            <span style={{ fontSize: 18 }}>💵</span>
            <div style={{ flex: 1, minWidth: isPhone ? 0 : 240, fontSize: 12, color: "#7a5400" }}>
              {showOnlyUnpaidGuard ? (
                <>Showing only invoices that need guard pay recorded.</>
              ) : (
                isPhone
                  ? <><strong>{guardUnpaidCount}</strong> need guard pay recorded</>
                  : <><strong>{guardUnpaidCount}</strong> invoice{guardUnpaidCount === 1 ? "" : "s"} in this view need guard pay recorded.</>
              )}
            </div>
            <button
              className="btn btn-sm"
              onClick={() => setShowOnlyUnpaidGuard((v) => !v)}
              style={{
                background: showOnlyUnpaidGuard ? "#EF9F27" : "transparent",
                color: showOnlyUnpaidGuard ? "#fff" : "#7a5400",
                borderColor: "#EF9F27", fontWeight: 700,
              }}
            >
              {showOnlyUnpaidGuard ? "✕ Show all" : "Show only unpaid"}
            </button>
          </div>
        )}

        {/* View toggle — Statements (payer+period checks) vs Per-guard
            (granular line items for payroll). */}
        <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center" }}>
          <button
            className={`btn btn-sm ${viewMode === "statements" ? "btn-blue" : ""}`}
            onClick={() => setViewMode("statements")}
            title="One row per payer + billing period — the check you collect"
          >🧾 Statements</button>
          <button
            className={`btn btn-sm ${viewMode === "perguard" ? "btn-blue" : ""}`}
            onClick={() => setViewMode("perguard")}
            title="Granular per-guard invoice list (for payroll detail)"
          >Per-guard view</button>
          {!isPhone && <span style={{ fontSize: 11, color: "#aaa", marginLeft: 6 }}>
            {viewMode === "statements"
              ? "Each row is one payer's bill for a cycle. Expand to see jobs."
              : "Every per-guard invoice line. Use the chips/filters below."}
          </span>}
        </div>

        {/* Status chip bar — filters statement status in Statements view,
            invoice status in Per-guard view. */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {[
            { key: "all", label: "All" },
            { key: "draft", label: "Draft" },
            { key: "sent", label: "Sent" },
            { key: "paid", label: "Paid" },
            ...(viewMode === "perguard" ? [{ key: "void", label: "Void" }] : []),
          ].map((opt) => (
            <button
              key={opt.key}
              className={`btn btn-sm ${statusFilter === opt.key ? "btn-blue" : ""}`}
              onClick={() => setStatusFilter(opt.key)}
              style={{ position: "relative" }}
            >
              {opt.label}
              {viewMode === "perguard" && (
                <span style={{ marginLeft: 4, opacity: 0.7 }}>({counts[opt.key]})</span>
              )}
            </button>
          ))}
        </div>

        {/* Pay cycle + broker dropdowns — per-guard view only (statements
            already organize by payer+period, so these are redundant there). */}
        {viewMode === "perguard" && (
        <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: 6 }}>
            Pay cycle:
            <select value={cycleFilter} onChange={(e) => setCycleFilter(e.target.value)} style={{ padding: "4px 8px" }}>
              <option value="all">All cycles</option>
              {cycleOptions.map((c) => (
                <option key={c} value={c}>{payCycleLabel(c)}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: 6 }}>
            Broker:
            <select value={brokerFilter} onChange={(e) => setBrokerFilter(e.target.value)} style={{ padding: "4px 8px" }}>
              <option value="all">All brokers</option>
              {Array.from(new Set(invoices.map((i) => i.brokerName).filter(Boolean))).sort().map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </label>
          {/* Search — paste an invoice #, job # / HAWB (NYW…), broker, or
              guard name to jump straight to its invoice. */}
          <div style={{ position: "relative" }}>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search invoice #, job #, HAWB…"
              style={{
                width: 220, padding: "5px 24px 5px 26px",
                border: "1px solid #ddd", borderRadius: 7, fontSize: 12,
              }}
            />
            <span style={{
              position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
              fontSize: 12, color: "#aaa", pointerEvents: "none",
            }}>🔍</span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                title="Clear search"
                style={{
                  position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", color: "#888", fontSize: 13,
                  cursor: "pointer", padding: 0, width: 18, height: 18,
                }}
              >✕</button>
            )}
          </div>
          {/* Aggregate-by-job toggle. Collapses per-guard rows into one
              row per job so the displayed Total matches the broker bill. */}
          <button
            className={`btn btn-sm ${groupByJob ? "btn-blue" : ""}`}
            onClick={() => setGroupByJob((v) => !v)}
            title="Collapse per-guard rows into one row per job (broker total)"
          >
            {groupByJob ? "✓ Grouped by job" : "Group by job"}
          </button>
          <span style={{ fontSize: 12, color: "#888", marginLeft: "auto" }}>
            Current cycle: <b>{payCycleLabel(payCycleFor(new Date()))}</b>
          </span>
        </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="empty-state"><div>Loading invoices…</div></div>
        ) : viewMode === "statements" ? (
          visibleStatements.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🧾</div>
              <div>No statements{statusFilter !== "all" ? ` with status "${statusFilter}"` : ""} yet.</div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                Approve jobs in Ready to Bill — they roll up into a statement per payer per cycle.
              </div>
            </div>
          ) : (
            <StatementsTable
              statements={visibleStatements}
              onViewJob={(jobId) => setViewJobId(jobId)}
              onMarkSent={async (invoiceIds) => {
                await markInvoicesSent(invoiceIds);
                setInvoices((prev) => prev.map((i) =>
                  invoiceIds.includes(i.id) ? { ...i, status: "sent" } : i
                ));
                flashToast(`Statement marked sent (${invoiceIds.length} invoice${invoiceIds.length === 1 ? "" : "s"})`);
              }}
              onMarkPaid={(invoiceIds, total, label) => {
                setPaymentDialog({
                  title: `Mark ${label} paid`,
                  defaultAmount: total,
                  invoiceIds,
                });
              }}
              onMoveJobs={handleMoveJobs}
              isPhone={isPhone}
            />
          )
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📄</div>
            <div>No invoices match the current filters.</div>
          </div>
        ) : groupByJob ? (
          <JobAggregatedTable
            invoices={filtered}
            isPhone={isPhone}
            onViewJob={(jobId) => setViewJobId(jobId)}
            onStatusBulk={async (invoiceIds, nextStatus) => {
              for (const id of invoiceIds) {
                // eslint-disable-next-line no-await-in-loop
                await setInvoiceStatus(id, nextStatus);
              }
              setInvoices((prev) => prev.map((i) =>
                invoiceIds.includes(i.id) ? { ...i, status: nextStatus } : i
              ));
              flashToast(`${invoiceIds.length} invoice${invoiceIds.length === 1 ? "" : "s"} → ${nextStatus}`);
            }}
            onPaidBulk={(invoiceIds, total) => {
              setPaymentDialog({
                title: `Mark ${invoiceIds.length} invoice${invoiceIds.length === 1 ? "" : "s"} paid`,
                defaultAmount: total,
                invoiceIds,
              });
            }}
          />
        ) : (
          <>
          {/* Bulk action bar — appears when at least one invoice is
              checked. Shows count + total of selected and offers the
              "Mark paid" bulk action. Clear-selection clears the set. */}
          {selectedIds.size > 0 && (() => {
            const selected = filtered.filter((i) => selectedIds.has(i.id));
            const total = selected.reduce((s, i) => s + (Number(i.total) || 0), 0);
            return (
              <div style={{
                margin: "0 0 10px", padding: "10px 14px",
                background: "#eef4fa", border: "1px solid #c8dcee", borderRadius: 8,
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
              }}>
                <span style={{ fontSize: 12, color: "#185FA5", fontWeight: 700 }}>
                  {selectedIds.size} selected · {formatMoney(total)}
                </span>
                <button
                  className="btn btn-sm"
                  onClick={() => setSelectedIds(new Set())}
                >Clear</button>
                <div style={{ flex: 1 }} />
                <button
                  className="btn btn-sm btn-green"
                  onClick={handleMarkSelectedPaid}
                >
                  💵 Mark selected paid
                </button>
              </div>
            );
          })()}

          <div className="panel" style={{ overflow: "auto" }}>
            <div style={{
              display: isPhone ? "none" : "grid",
              gridTemplateColumns: PERGUARD_COLS, minWidth: PERGUARD_MIN_WIDTH,
              gap: 10, padding: "10px 14px", borderBottom: "1px solid #eee",
              fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase",
            }}>
              {/* Master select-all: ticks every invoice in the current
                  filtered view. Indeterminate state when some-but-not-
                  all rows are selected. */}
              <input
                type="checkbox"
                style={{ cursor: "pointer", width: 16, height: 16 }}
                checked={filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id))}
                ref={(el) => {
                  if (!el) return;
                  const some = filtered.some((i) => selectedIds.has(i.id));
                  const all = filtered.every((i) => selectedIds.has(i.id));
                  el.indeterminate = some && !all;
                }}
                onChange={(e) => {
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) filtered.forEach((i) => next.add(i.id));
                    else filtered.forEach((i) => next.delete(i.id));
                    return next;
                  });
                }}
              />
              <span>Invoice #</span>
              <span>Job #</span>
              <span>Broker / Guard</span>
              <span>Type</span>
              <span>Date</span>
              <span style={{ textAlign: "right" }}>Hours</span>
              <span style={{ textAlign: "right" }}>Miles</span>
              <span style={{ textAlign: "right" }}>Total</span>
              <span>Cycle</span>
              <span>Status</span>
              <span style={{ textAlign: "right" }}>Actions</span>
            </div>
            {(() => {
              // Group invoices that share a bundleId so DAI-style schedule
              // submissions can be sent in one click. Single invoices stay as
              // 1-item bundles (no header rendered for those).
              const bundles = [];
              const byBundle = new Map();
              for (const inv of filtered) {
                const key = inv.bundleId || `solo:${inv.id}`;
                if (!byBundle.has(key)) {
                  const b = {
                    bundleId: inv.bundleId || null,
                    isReal: !!inv.bundleId,
                    brokerName: inv.brokerName,
                    salesRepName: inv.salesRepName || "",
                    invoices: [],
                    totalAmount: 0,
                    allDraft: true,
                  };
                  byBundle.set(key, b);
                  bundles.push(b);
                } else if (inv.salesRepName && !byBundle.get(key).salesRepName) {
                  byBundle.get(key).salesRepName = inv.salesRepName;
                }
                const b = byBundle.get(key);
                b.invoices.push(inv);
                b.totalAmount += Number(inv.total) || 0;
                if (inv.status !== "draft") b.allDraft = false;
                // Pull rep name from job if present (we'd ideally store on invoice)
                // For now use guardName as a fallback; a real fix would copy salesRepName
                // onto the invoice doc at creation.
              }

              return bundles.map((b) => {
                const isMultiBundle = b.isReal && b.invoices.length > 1;
                return (
                  <React.Fragment key={b.bundleId || b.invoices[0].id}>
                    {isMultiBundle && (
                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 10, padding: "8px 14px",
                        background: "#eef3fb", borderTop: "2px solid #d6e0ee",
                        borderBottom: "1px solid #d6e0ee",
                        fontSize: 11, color: "#185FA5", fontWeight: 700,
                      }}>
                        <span>
                          📦 {b.brokerName}
                          {b.salesRepName ? ` · ${b.salesRepName}` : ""}
                          {" · "}{b.invoices.length} jobs · {formatMoney(b.totalAmount)}
                        </span>
                        {b.allDraft && (
                          <button className="btn btn-sm btn-blue"
                            onClick={() => handleBundleSend(b)}
                            title="Open email to AP with all jobs listed; marks them all sent">
                            📧 Bundle &amp; email
                          </button>
                        )}
                      </div>
                    )}
                    {b.invoices.map((inv) => {
              const sLabel = STATUS_LABELS[inv.status] || STATUS_LABELS.draft;
              // Highlight rows where the guard hasn't been paid yet
              // (and the invoice isn't void). Amber left-edge stripe so
              // it's hard to miss while scrolling — the goal is "I'd
              // never accidentally forget to record a guard payment."
              const guardUnpaid = inv.guardPaidAmount == null && inv.status !== "void";
              if (isPhone) {
                const jobDate = (() => {
                  const [y, mo, d] = String(inv.scheduledDate || "").split("-").map(Number);
                  return y && mo && d ? `${mo}/${d}/${String(y).slice(2)}` : formatDate(inv.completedAt || inv.createdAt);
                })();
                const toggleSel = () => setSelectedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(inv.id)) next.delete(inv.id); else next.add(inv.id);
                  return next;
                });
                return (
                  <div key={inv.id} style={{
                    padding: "12px 14px", borderBottom: "1px solid #f0f0f0",
                    borderLeft: guardUnpaid ? "3px solid #EF9F27" : "3px solid transparent",
                    background: selectedIds.has(inv.id) ? "#eef4fa" : guardUnpaid ? "#fffbf2" : "#fff",
                  }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <input type="checkbox" checked={selectedIds.has(inv.id)} onChange={toggleSel} style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }} onClick={() => inv.jobId && setViewJobId(inv.jobId)}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <span style={{ fontWeight: 800, color: "#185FA5", overflowWrap: "anywhere" }}>{invoiceLabel(inv) || inv.invoiceNumber}</span>
                          <span style={{ fontWeight: 800 }}>{formatMoney(inv.total)}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7684", marginTop: 2 }}>
                          {jobDate} · {inv.brokerName}{inv.guardName ? ` · ${inv.guardName}` : ""} · {formatHours(inv.hoursCharged)} hrs
                        </div>
                        <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>
                          {inv.invoiceNumber} · <span style={{ fontWeight: 700, color: sLabel.color }}>{sLabel.label}</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                      {inv.status === "draft" && (
                        <button className="btn btn-sm btn-blue" style={{ flex: 1, minHeight: 40 }} onClick={() => handleStatus(inv, "sent")}>Mark sent</button>
                      )}
                      {inv.status === "sent" && (
                        <button className="btn btn-sm btn-green" style={{ flex: 1, minHeight: 40 }} onClick={() => handleStatus(inv, "paid")}>Broker paid</button>
                      )}
                      {inv.guardPaidAmount != null ? (
                        <button className="btn btn-sm" style={{ flex: 1, minHeight: 40, background: "#E0F4E8", color: "#0E7C46", borderColor: "#a9d4c2" }}
                          onClick={() => handleClearGuardPay(inv)}>✓ Guard paid {formatMoney(inv.guardPaidAmount)}</button>
                      ) : inv.status !== "void" && (
                        <button className="btn btn-sm" style={{ flex: 1, minHeight: 40, background: "#fff3cd", color: "#856404", borderColor: "#EF9F27", fontWeight: 700 }}
                          onClick={() => handleGuardPay(inv)}>💵 Guard pay</button>
                      )}
                      <button className="btn btn-sm" style={{ minHeight: 40 }} onClick={() => handleDownload(inv)} title="Re-download workbook + POD PDF">📦</button>
                      {inv.status !== "void" ? (
                        <button className="btn btn-sm" style={{ minHeight: 40 }} onClick={() => {
                          if (window.confirm(`Void invoice ${inv.invoiceNumber}?`)) handleStatus(inv, "void");
                        }} title="Void">✕</button>
                      ) : (
                        <button className="btn btn-sm" style={{ minHeight: 40 }} onClick={() => handleStatus(inv, "draft")} title="Restore to draft">↺</button>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div key={inv.id} style={{
                  display: "grid",
                  gridTemplateColumns: PERGUARD_COLS, minWidth: PERGUARD_MIN_WIDTH,
                  gap: 10, padding: "10px 14px", borderBottom: "1px solid #f5f5f5",
                  alignItems: "center", fontSize: 12,
                  borderLeft: guardUnpaid ? "3px solid #EF9F27" : "3px solid transparent",
                  background: selectedIds.has(inv.id)
                    ? "#eef4fa"
                    : guardUnpaid ? "#fffbf2" : "transparent",
                }}>
                  <input
                    type="checkbox"
                    style={{ cursor: "pointer", width: 16, height: 16 }}
                    checked={selectedIds.has(inv.id)}
                    onChange={(e) => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(inv.id);
                        else next.delete(inv.id);
                        return next;
                      });
                    }}
                  />
                  {/* Invoice # is clickable when we have a jobId on
                      record — opens JobDetailModal so the owner can see
                      the route, packages, hours, etc. without leaving
                      the page. Critical for Ferrari/Executive invoices
                      which don't carry a broker-issued job number. */}
                  {inv.jobId ? (
                    <button
                      type="button"
                      onClick={() => setViewJobId(inv.jobId)}
                      title="View underlying job"
                      style={{
                        fontFamily: "monospace", color: "#185FA5", fontWeight: 600,
                        background: "none", border: "none", padding: 0,
                        cursor: "pointer", textAlign: "left", textDecoration: "underline",
                        textDecorationStyle: "dotted", textDecorationColor: "#A8C5E4",
                      }}
                    >{inv.invoiceNumber}</button>
                  ) : (
                    <span style={{ fontFamily: "monospace", color: "#185FA5", fontWeight: 600 }}>
                      {inv.invoiceNumber}
                    </span>
                  )}
                  <span style={{ fontFamily: "monospace" }}>
                    {invoiceLabel(inv) || (inv.jobId ? (
                      // No broker-issued job# (Ferrari, Executive). Surface
                      // a tiny "View job" link so the owner can drill in
                      // even if they didn't notice the invoice# is clickable.
                      <button
                        type="button"
                        onClick={() => setViewJobId(inv.jobId)}
                        style={{
                          fontFamily: "inherit", fontSize: 10, color: "#185FA5",
                          background: "none", border: "none", padding: 0,
                          cursor: "pointer", textDecoration: "underline",
                        }}
                      >View job →</button>
                    ) : "")}
                  </span>
                  <span>
                    <div style={{ fontWeight: 600 }}>{inv.brokerName}</div>
                    <div style={{ fontSize: 10, color: "#888" }}>
                      G{inv.guardNumber}{inv.guardName ? ` · ${inv.guardName}` : ""}
                      {inv.isDriver ? " · driver" : ""}
                    </div>
                  </span>
                  <span style={{ fontSize: 11, textTransform: "uppercase", color: "#666" }}>{inv.jobType}</span>
                  <span title={inv.scheduledDate ? "Scheduled job date" : (inv.completedAt ? "Job completed" : "Invoice created (no job date)")}>
                    {(() => {
                      // Prefer scheduledDate — when the job was set to run —
                      // since that's the canonical "this job happened on
                      // this day" reference. Fallback chain for older
                      // invoices that don't have it yet.
                      if (inv.scheduledDate) {
                        const [y, mo, d] = String(inv.scheduledDate).split("-").map(Number);
                        if (y && mo && d) {
                          return `${mo}/${d}/${String(y).slice(2)}`;
                        }
                      }
                      return formatDate(inv.completedAt || inv.createdAt);
                    })()}
                  </span>
                  <span style={{ textAlign: "right", color: "#555" }} title="Hours billed on this guard's line">
                    {formatHours(inv.hoursCharged)}
                  </span>
                  <span style={{ textAlign: "right", color: "#555" }} title="Miles billed on this guard's line">
                    {Number(inv.miles) || 0}
                  </span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatMoney(inv.total)}</span>
                  <span style={{ fontSize: 10, color: "#666" }}>{(inv.payCycle || "").slice(5)}</span>
                  <span>
                    <span
                      title={inv.status === "paid" && (inv.paymentReference || inv.paymentReceivedAt)
                        ? `Paid${inv.paymentReference ? ` · ref ${inv.paymentReference}` : ""}${inv.paymentReceivedAt ? ` · received ${formatDate(inv.paymentReceivedAt)}` : ""}`
                        : undefined}
                      style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                        color: sLabel.color, background: sLabel.bg,
                        cursor: inv.status === "paid" && inv.paymentReference ? "help" : "default",
                      }}>{sLabel.label}</span>
                  </span>
                  <span style={{ display: "flex", gap: 4, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {inv.gmailDraftId && (
                      <a
                        href={inv.gmailDraftAuthEmail
                          ? `https://mail.google.com/mail/?authuser=${encodeURIComponent(inv.gmailDraftAuthEmail)}#drafts/${inv.gmailDraftId}`
                          : `https://mail.google.com/mail/u/0/#drafts/${inv.gmailDraftId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-blue"
                        title={`Open the Gmail draft${inv.gmailDraftAuthEmail ? ` in ${inv.gmailDraftAuthEmail}` : ""}`}
                        style={{ textDecoration: "none" }}
                      >📧</a>
                    )}
                    <button className="btn btn-sm" onClick={() => handleDownload(inv)} title="Re-download workbook + POD PDF">📦</button>
                    {inv.guardPaidAmount != null ? (
                      <button
                        className="btn btn-sm"
                        onClick={() => handleClearGuardPay(inv)}
                        title={`Guard paid ${formatMoney(inv.guardPaidAmount)}${inv.guardPaidAt ? " on " + formatDate(inv.guardPaidAt) : ""} — click to edit`}
                        style={{ background: "#E0F4E8", color: "#0E7C46", borderColor: "#a9d4c2" }}
                      >✓ Guard paid {formatMoney(inv.guardPaidAmount)}</button>
                    ) : (
                      <button
                        className="btn btn-sm"
                        onClick={() => handleGuardPay(inv)}
                        title={`Record what you paid ${inv.guardName || `Guard ${inv.guardNumber}`}`}
                        // Amber styling so the unpaid TODO is impossible
                        // to miss while scrolling. Matches the row's
                        // left-edge stripe color.
                        style={{
                          background: "#fff3cd", color: "#856404",
                          borderColor: "#EF9F27", fontWeight: 700,
                        }}
                      >💵 Record guard pay</button>
                    )}
                    {inv.status === "draft" && (
                      <button className="btn btn-sm btn-blue" onClick={() => handleStatus(inv, "sent")}>Sent</button>
                    )}
                    {inv.status === "sent" && (
                      <button className="btn btn-sm btn-green" onClick={() => handleStatus(inv, "paid")}>Paid</button>
                    )}
                    {inv.status !== "void" && (
                      <button className="btn btn-sm" onClick={() => {
                        if (window.confirm(`Void invoice ${inv.invoiceNumber}?`)) handleStatus(inv, "void");
                      }} title="Void">✕</button>
                    )}
                    {inv.status === "void" && (
                      <button className="btn btn-sm" onClick={() => handleStatus(inv, "draft")} title="Restore to draft">↺</button>
                    )}
                  </span>
                </div>
              );
            })}
                  </React.Fragment>
                );
              });
            })()}
          </div>
          </>
        )}
      </div>

      {/* Mark-paid dialog — small modal for entering check info before
          bulk-marking invoices paid. Triggered by "Mark cycle paid" on
          the summary card or "Mark selected paid" on the bulk action
          bar that appears when invoices are checked in the list. */}
      {paymentDialog && (
        <PaymentDialog
          title={paymentDialog.title}
          defaultAmount={paymentDialog.defaultAmount}
          onCancel={() => setPaymentDialog(null)}
          onConfirm={async ({ reference, receivedAt, amount }) => {
            try {
              await markInvoicesPaid(paymentDialog.invoiceIds, {
                paymentReference: reference,
                paymentReceivedAt: receivedAt,
                paymentAmount: amount,
              });
              // Optimistic local flip so the UI reacts instantly.
              setInvoices((prev) => prev.map((i) =>
                paymentDialog.invoiceIds.includes(i.id)
                  ? { ...i, status: "paid", paymentReference: reference,
                       paymentReceivedAt: new Date(receivedAt).toISOString() }
                  : i
              ));
              setSelectedIds(new Set());
              flashToast(`Marked ${paymentDialog.invoiceIds.length} invoice${paymentDialog.invoiceIds.length === 1 ? "" : "s"} paid`);
            } catch (err) {
              alert("Failed to mark paid: " + (err?.message || err));
            }
            setPaymentDialog(null);
          }}
        />
      )}

      {/* Read-only job preview opened by clicking an invoice number or
          "View job →" link. Same modal used on Job Inbox so the owner
          gets a familiar surface with stops, packages, HAWBs, POD
          photos, etc. — without any nav. */}
      {viewJobId && (
        <JobDetailModal
          jobId={viewJobId}
          companyId={userProfile?.companyId}
          onClose={() => setViewJobId(null)}
        />
      )}
    </div>
  );
}

// Small modal — gather check info before bulk-marking invoices paid.
// Used both from the cycle-summary "Mark cycle paid" button and the
// bulk multi-select action.
function PaymentDialog({ title, defaultAmount, onCancel, onConfirm }) {
  const [reference, setReference] = useState("");
  const today = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const [receivedAt, setReceivedAt] = useState(today);
  const [amount, setAmount] = useState(String(defaultAmount?.toFixed(2) ?? ""));
  const [saving, setSaving] = useState(false);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      zIndex: 250, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onCancel}>
      <div style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 420,
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
        </div>
        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Reference</label>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Check #, wire confirmation, Zelle reference..."
              autoFocus
            />
            <div style={{ fontSize: 10, color: "#888" }}>
              How the dispatcher paid. Recorded on every invoice in this batch.
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Received on</label>
              <input
                type="date"
                value={receivedAt}
                onChange={(e) => setReceivedAt(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Amount</label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
            <button
              className="btn btn-green"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                await onConfirm({
                  reference: reference.trim(),
                  receivedAt,
                  amount: Number(amount) || 0,
                });
                setSaving(false);
              }}
            >
              {saving ? "Marking..." : "💵 Mark paid"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Aggregate-by-job table ─────────────────────────────────────────────
// When "Group by job" is on, the Invoices page renders this instead of
// the per-guard rows. One row per jobId, showing the broker bill total
// (sum of every guard's invoice for that job), the per-guard list, and
// a single derived status. Actions apply to every underlying invoice
// in the group at once.
function JobAggregatedTable({ invoices, onViewJob, onStatusBulk, onPaidBulk, isPhone = false }) {
  // Group filtered invoices by jobId. Invoices that have NO jobId (rare:
  // legacy data from before jobId was stamped on every invoice) bucket
  // under their own one-off keys so they still show up.
  const groups = (() => {
    const map = new Map();
    for (const inv of invoices) {
      const key = inv.jobId || `solo:${inv.id}`;
      const cur = map.get(key) || { key, jobId: inv.jobId || null, invoices: [] };
      cur.invoices.push(inv);
      map.set(key, cur);
    }
    return Array.from(map.values());
  })();

  // Derive a single status label per job-group from the underlying
  // invoices. Order of dominance: void → draft > sent > paid. (A job
  // where one guard's slice is still draft can't be considered "sent.")
  function deriveStatus(invs) {
    if (invs.every((i) => i.status === "void")) return "void";
    if (invs.some((i) => i.status === "draft")) return "draft";
    if (invs.some((i) => i.status === "sent")) return "sent";
    if (invs.every((i) => i.status === "paid")) return "paid";
    return "draft";
  }

  return (
    <div className="panel" style={{ overflow: "auto" }}>
      <div style={{
        display: isPhone ? "none" : "grid",
        gridTemplateColumns: GROUPED_COLS, minWidth: GROUPED_MIN_WIDTH,
        gap: 10, padding: "10px 14px", borderBottom: "1px solid #eee",
        fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase",
      }}>
        <span>Invoice #s</span>
        <span>Job #</span>
        <span>Broker / Guards</span>
        <span>Type</span>
        <span>Date</span>
        <span style={{ textAlign: "right" }}>Hours</span>
        <span style={{ textAlign: "right" }}>Miles</span>
        <span style={{ textAlign: "right" }}>Total</span>
        <span>Cycle</span>
        <span>Status</span>
        <span style={{ textAlign: "right" }}>Actions</span>
      </div>
      {groups.map((g) => {
        const first = g.invoices[0];
        const total = g.invoices.reduce((s, i) => s + (Number(i.total) || 0), 0);
        // Hours across the whole job. Guards on the same job can bill
        // different hours (a relieved guard leaves the airport early), so
        // the column sums them and the tooltip breaks it down per guard.
        const jobHours = g.invoices.reduce((s, i) => s + (Number(i.hoursCharged) || 0), 0);
        const hoursTitle = g.invoices.length > 1
          ? `Hours billed: ${g.invoices
              .slice()
              .sort((a, b) => (a.guardNumber || 0) - (b.guardNumber || 0))
              .map((i) => `G${i.guardNumber} ${formatHours(i.hoursCharged)}`)
              .join(" · ")}`
          : "Hours billed on this job";
        // Miles across the job — each own-car guard bills its own mileage,
        // so the total is the sum; the tooltip breaks it down per guard.
        const jobMiles = g.invoices.reduce((s, i) => s + (Number(i.miles) || 0), 0);
        const milesTitle = g.invoices.length > 1
          ? `Miles billed: ${g.invoices
              .slice()
              .sort((a, b) => (a.guardNumber || 0) - (b.guardNumber || 0))
              .map((i) => `G${i.guardNumber} ${Number(i.miles) || 0}`)
              .join(" · ")}`
          : "Miles billed on this job";
        const status = deriveStatus(g.invoices);
        const sLabel = STATUS_LABELS[status] || STATUS_LABELS.draft;
        // Per-guard summary like "G1 Scott · G2 (open)" so the owner
        // can still see who was assigned without leaving the page.
        const guardSummary = g.invoices
          .slice()
          .sort((a, b) => (a.guardNumber || 0) - (b.guardNumber || 0))
          .map((i) => `G${i.guardNumber}${i.guardName ? " " + i.guardName.split(" ")[0] : ""}`)
          .join(" · ");
        const invoiceIds = g.invoices.map((i) => i.id);

        // Date column: prefer scheduledDate; fall back to completedAt/createdAt.
        const dateLabel = (() => {
          if (first.scheduledDate) {
            const [y, mo, d] = String(first.scheduledDate).split("-").map(Number);
            if (y && mo && d) return `${mo}/${d}/${String(y).slice(2)}`;
          }
          return formatDate(first.completedAt || first.createdAt);
        })();

        // Phone: the job as a card — same actions as the row below.
        if (isPhone) return (
          <div key={g.key} style={{ padding: "12px 14px", borderBottom: "1px solid #f0f0f0" }}>
            <div onClick={() => g.jobId && onViewJob(g.jobId)} style={{ cursor: g.jobId ? "pointer" : "default" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontWeight: 800, color: "#185FA5", overflowWrap: "anywhere" }}>{invoiceLabel(first) || "View job"}</span>
                <span style={{ fontWeight: 800 }}>{formatMoney(total)}</span>
              </div>
              <div style={{ fontSize: 12, color: "#6b7684", marginTop: 2 }}>
                {dateLabel} · {first.brokerName || "—"} · {guardSummary || "—"} · {formatHours(jobHours)} hrs
              </div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>
                {g.invoices.map((i) => i.invoiceNumber).join(", ")} · <span style={{ fontWeight: 700, color: sLabel.color }}>{sLabel.label}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              {status === "draft" && (
                <button className="btn btn-sm btn-blue" style={{ flex: 1, minHeight: 40 }} onClick={() => onStatusBulk(invoiceIds, "sent")}>Mark sent</button>
              )}
              {status === "sent" && (
                <button className="btn btn-sm btn-green" style={{ flex: 1, minHeight: 40 }} onClick={() => onPaidBulk(invoiceIds, total)}>Record payment</button>
              )}
              {status !== "void" ? (
                <button className="btn btn-sm" style={{ minHeight: 40 }} title="Void this job's invoices" onClick={() => {
                  const live = g.invoices.filter((i) => i.status !== "void").map((i) => i.id);
                  if (window.confirm(`Void ${live.length} invoice${live.length === 1 ? "" : "s"} on ${invoiceLabel(first) || "this job"}? The job unlocks so you can re-bill it from Ready to Bill.`)) onStatusBulk(live, "void");
                }}>✕</button>
              ) : (
                <button className="btn btn-sm" style={{ minHeight: 40 }} title="Restore to draft" onClick={() => onStatusBulk(invoiceIds, "draft")}>↺</button>
              )}
            </div>
          </div>
        );

        return (
          <div key={g.key} style={{
            display: "grid",
            gridTemplateColumns: GROUPED_COLS, minWidth: GROUPED_MIN_WIDTH,
            gap: 10, padding: "10px 14px", borderBottom: "1px solid #f5f5f5",
            alignItems: "center", fontSize: 12,
          }}>
            <span style={{ fontFamily: "monospace", color: "#888", fontSize: 10 }}>
              {g.invoices.map((i) => i.invoiceNumber).join(", ")}
            </span>
            <span style={{ fontFamily: "monospace" }}>
              {invoiceLabel(first) || (g.jobId ? (
                <button
                  type="button"
                  onClick={() => onViewJob(g.jobId)}
                  style={{
                    fontFamily: "inherit", fontSize: 10, color: "#185FA5",
                    background: "none", border: "none", padding: 0,
                    cursor: "pointer", textDecoration: "underline",
                  }}
                >View job →</button>
              ) : "—")}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{first.brokerName || "—"}</div>
              <div style={{ fontSize: 10, color: "#888" }}>{guardSummary || "—"}</div>
            </div>
            <span style={{ fontSize: 11, textTransform: "uppercase", color: "#666" }}>{first.jobType || "—"}</span>
            <span>{dateLabel}</span>
            <span style={{ textAlign: "right", color: "#555" }} title={hoursTitle}>
              {formatHours(jobHours)}
            </span>
            <span style={{ textAlign: "right", color: "#555" }} title={milesTitle}>
              {jobMiles}
            </span>
            <span style={{ textAlign: "right", fontWeight: 700 }}>{formatMoney(total)}</span>
            <span style={{ fontSize: 10, color: "#666" }}>{(first.payCycle || "").slice(5)}</span>
            <span>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                color: sLabel.color, background: sLabel.bg,
              }}>{sLabel.label}</span>
            </span>
            <span style={{ display: "flex", gap: 4, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {g.jobId && (
                <button className="btn btn-sm" onClick={() => onViewJob(g.jobId)} title="View underlying job">
                  👁 Job
                </button>
              )}
              {status === "draft" && (
                <button className="btn btn-sm btn-blue" onClick={() => onStatusBulk(invoiceIds, "sent")}>
                  Sent ({invoiceIds.length})
                </button>
              )}
              {status === "sent" && (
                <button className="btn btn-sm btn-green" onClick={() => onPaidBulk(invoiceIds, total)}>
                  Paid ({invoiceIds.length})
                </button>
              )}
              {/* Void every live invoice on this job — same ✕ as the
                  per-guard rows, so the owner doesn't have to flip the
                  Group-by-job toggle just to void. Voiding the last live
                  invoice auto-unlocks the job for Ready-to-Bill. */}
              {status !== "void" && (
                <button
                  className="btn btn-sm"
                  title="Void this job's invoices (unlocks the job for re-billing)"
                  onClick={() => {
                    const live = g.invoices.filter((i) => i.status !== "void").map((i) => i.id);
                    if (window.confirm(
                      `Void ${live.length} invoice${live.length === 1 ? "" : "s"} on ${invoiceLabel(first) || "this job"}? ` +
                      "The job unlocks so you can re-bill it from Ready to Bill."
                    )) {
                      onStatusBulk(live, "void");
                    }
                  }}
                >✕</button>
              )}
              {status === "void" && (
                <button
                  className="btn btn-sm"
                  title="Restore to draft"
                  onClick={() => onStatusBulk(invoiceIds, "draft")}
                >↺</button>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Statements table ────────────────────────────────────────────────
// Payer-centric view: one expandable row per (payer, billing period) =
// the single check the owner collects. Expand reveals the jobs in that
// statement (not per-guard — that's the "Per-guard view" toggle).
// Status + Mark sent/paid actions cascade to every underlying invoice.
function StatementsTable({ statements, onViewJob, onMarkSent, onMarkPaid, onMoveJobs, isPhone = false }) {
  const [openKeys, setOpenKeys] = useState(() => new Set());
  function toggle(key) {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // Check-reconciliation selection: checkboxes on the job rows of ONE
  // expanded statement at a time; a move bar appears with a target
  // period picker. Jobs with a paid invoice can't move (unmark paid
  // first) so paid check totals can never drift.
  const [moveSel, setMoveSel] = useState(null); // { key: statement key, jobKeys: Set }
  const [moveTargetId, setMoveTargetId] = useState("");
  const [moving, setMoving] = useState(false);
  const jobKeyOf = (j) => j.jobId || j.invoices?.[0]?.id || j.jobNumber;

  function toggleJobForMove(st, jk) {
    if (!moveSel || moveSel.key !== st.key) {
      // First checkbox in this statement — default target: next period.
      setMoveTargetId(shiftDispatcherCycle(st.cycle, 1).id);
      setMoveSel({ key: st.key, jobKeys: new Set([jk]) });
      return;
    }
    const next = new Set(moveSel.jobKeys);
    if (next.has(jk)) next.delete(jk); else next.add(jk);
    setMoveSel(next.size ? { key: moveSel.key, jobKeys: next } : null);
  }

  async function handleMoveClick(st, targets) {
    if (!moveSel || moveSel.key !== st.key || moveSel.jobKeys.size === 0) return;
    const target = targets.find((c) => c.id === moveTargetId) || targets[0];
    const clusters = st.jobs.filter((j) => moveSel.jobKeys.has(jobKeyOf(j)));
    setMoving(true);
    await onMoveJobs(clusters, target);
    setMoving(false);
    setMoveSel(null);
  }

  const fmtJobDate = (s) => {
    if (s.scheduledDate) {
      const [y, mo, d] = String(s.scheduledDate).split("-").map(Number);
      if (y && mo && d) return `${mo}/${d}/${String(y).slice(2)}`;
    }
    return formatDate(s.completedAt);
  };

  // Phone: one card per statement with the next step as a single button
  // (same Sent / Paid actions as the table); tap the card for its jobs.
  // Moving jobs between periods stays on the desktop table.
  if (isPhone) {
    return (
      <div>
        {statements.map((st) => {
          const isOpen = openKeys.has(st.key);
          const sLabel = STATUS_LABELS[st.status] || STATUS_LABELS.draft;
          const draftIds = st.invoices.filter((i) => i.status === "draft").map((i) => i.id);
          const unpaidIds = st.invoices.filter((i) => i.status !== "paid").map((i) => i.id);
          const unpaidTotal = st.invoices.filter((i) => i.status !== "paid").reduce((s, i) => s + (Number(i.total) || 0), 0);
          return (
            <div key={st.key} className="panel" style={{ padding: "12px 14px", marginBottom: 8 }}>
              <div onClick={() => toggle(st.key)} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: 15, color: "#111" }}>{st.payer}</span>
                  <span style={{ fontWeight: 800, fontSize: 17 }}>{formatMoney(st.total)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 3, fontSize: 12, color: "#6b7684" }}>
                  <span>{st.cycle.label} · {st.jobCount} job{st.jobCount === 1 ? "" : "s"} {isOpen ? "▴" : "▾"}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10, color: sLabel.color, background: sLabel.bg }}>{sLabel.label}</span>
                </div>
              </div>
              {isOpen && (
                <div style={{ marginTop: 10, borderTop: "1px solid #eef0f3" }}>
                  {st.jobs.map((j, ji) => (
                    <div key={j.jobId || ji} onClick={() => j.jobId && onViewJob(j.jobId)}
                      style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: "1px solid #f4f5f7", cursor: j.jobId ? "pointer" : "default" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#185FA5", overflowWrap: "anywhere" }}>{invoiceLabel(j) || "View job"}</div>
                        <div style={{ fontSize: 12, color: "#6b7684" }}>{[fmtJobDate(j), j.routeSummary].filter(Boolean).join(" · ")}</div>
                      </div>
                      <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>{formatMoney(j.total)}</span>
                    </div>
                  ))}
                </div>
              )}
              {draftIds.length > 0 && (
                <button className="btn btn-blue" style={{ width: "100%", minHeight: 44, marginTop: 10 }}
                  onClick={() => onMarkSent(draftIds)}>Mark sent</button>
              )}
              {st.status === "sent" && (
                <button className="btn btn-green" style={{ width: "100%", minHeight: 44, marginTop: 10 }}
                  onClick={() => onMarkPaid(unpaidIds, unpaidTotal, `${st.payer} · ${st.cycle.label}`)}>Record payment</button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="panel" style={{ overflow: "auto" }}>
      <div style={{
        display: "grid", gridTemplateColumns: "24px 1.2fr 70px 110px 110px 200px",
        gap: 10, padding: "10px 14px", borderBottom: "1px solid #eee",
        fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase",
      }}>
        <span />
        <span>Payer · Period</span>
        <span style={{ textAlign: "center" }}>Jobs</span>
        <span style={{ textAlign: "right" }}>Total</span>
        <span>Status</span>
        <span style={{ textAlign: "right" }}>Actions</span>
      </div>
      {statements.map((st) => {
        const isOpen = openKeys.has(st.key);
        const sLabel = STATUS_LABELS[st.status] || STATUS_LABELS.draft;
        const allIds = st.invoices.map((i) => i.id);
        const draftIds = st.invoices.filter((i) => i.status === "draft").map((i) => i.id);
        const unpaidIds = st.invoices.filter((i) => i.status !== "paid").map((i) => i.id);
        const unpaidTotal = st.invoices
          .filter((i) => i.status !== "paid")
          .reduce((s, i) => s + (Number(i.total) || 0), 0);
        // Periods offered as move targets: ±3 cycles around this
        // statement, PLUS the cycle containing today and the one after
        // it — a late-approved job may need to jump several statements
        // forward to the currently-open one, which the ±window alone
        // missed. Deduped by id, oldest first, this statement excluded.
        const moveTargets = (() => {
          const seen = new Set([st.cycle.id]);
          const out = [];
          const push = (c) => { if (c && !seen.has(c.id)) { seen.add(c.id); out.push(c); } };
          [-3, -2, -1, 1, 2, 3].forEach((n) => push(shiftDispatcherCycle(st.cycle, n)));
          const nowCycle = dispatcherCycleFor(st.cycle.dispatcherName, new Date());
          push(nowCycle);
          push(shiftDispatcherCycle(nowCycle, 1));
          return out.sort((a, b) => a.startMs - b.startMs);
        })();
        const selHere = moveSel?.key === st.key ? moveSel.jobKeys : null;
        return (
          <React.Fragment key={st.key}>
            <div
              onClick={() => toggle(st.key)}
              style={{
                display: "grid", gridTemplateColumns: "24px 1.2fr 70px 110px 110px 200px",
                gap: 10, padding: "12px 14px", borderBottom: "1px solid #f5f5f5",
                alignItems: "center", fontSize: 12, cursor: "pointer",
                background: isOpen ? "#FAFBFC" : "#fff",
              }}
              title={isOpen ? "Collapse" : "Show jobs in this statement"}
            >
              <span style={{
                color: "#888", fontSize: 11, display: "inline-block",
                transition: "transform 0.15s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              }}>▶</span>
              <div>
                <div style={{ fontWeight: 700, color: "#111" }}>{st.payer}</div>
                <div style={{ fontSize: 10, color: "#888" }}>{st.cycle.label}</div>
              </div>
              <span style={{ textAlign: "center", color: "#444" }}>{st.jobCount}</span>
              <span style={{ textAlign: "right", fontWeight: 700 }}>{formatMoney(st.total)}</span>
              <span>
                <span
                  title={st.status === "paid" && (st.paidRef || st.paidAt)
                    ? `Paid${st.paidRef ? ` · ref ${st.paidRef}` : ""}${st.paidAt ? ` · ${formatDate(st.paidAt)}` : ""}`
                    : undefined}
                  style={{
                    fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                    color: sLabel.color, background: sLabel.bg,
                    cursor: st.status === "paid" && st.paidRef ? "help" : "default",
                  }}>{sLabel.label}</span>
              </span>
              <span style={{ display: "flex", gap: 4, justifyContent: "flex-end", flexWrap: "wrap" }}>
                {draftIds.length > 0 && (
                  <button
                    className="btn btn-sm btn-blue"
                    onClick={(e) => { e.stopPropagation(); onMarkSent(draftIds); }}
                    title="Mark every draft invoice in this statement as sent"
                  >Sent</button>
                )}
                {st.status === "sent" && (
                  <button
                    className="btn btn-sm btn-green"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkPaid(unpaidIds, unpaidTotal, `${st.payer} · ${st.cycle.label}`);
                    }}
                    title="Record the check and mark the whole statement paid"
                  >Paid</button>
                )}
              </span>
            </div>

            {isOpen && (
              <div style={{ padding: "4px 14px 12px 38px", borderBottom: "1px solid #f0f0f0", background: "#FAFBFC" }}>
                {selHere && selHere.size > 0 && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    padding: "8px 10px", marginBottom: 8, fontSize: 11,
                    background: "#EEF4FF", border: "1px solid #C9DCF0", borderRadius: 6,
                  }}>
                    <b>{selHere.size} job{selHere.size === 1 ? "" : "s"} selected</b>
                    <span style={{ color: "#666" }}>Move to</span>
                    <select
                      value={moveTargetId}
                      onChange={(e) => setMoveTargetId(e.target.value)}
                      style={{ padding: "4px 8px", fontSize: 11 }}
                    >
                      {moveTargets.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                    <button
                      className="btn btn-sm btn-blue"
                      disabled={moving}
                      onClick={() => handleMoveClick(st, moveTargets)}
                      title="Re-bill the selected jobs on the chosen statement (their actual job dates don't change)"
                    >{moving ? "Moving..." : "Move"}</button>
                    <button className="btn btn-sm" disabled={moving} onClick={() => setMoveSel(null)}>Cancel</button>
                  </div>
                )}
                <div style={{ border: "1px solid #e8e8e8", borderRadius: 6, overflow: "hidden", background: "#fff" }}>
                  <div style={{
                    display: "grid", gridTemplateColumns: "24px 70px 105px 1fr 1.1fr 50px 50px 52px 70px 90px",
                    padding: "6px 10px", background: "#f8f8f8",
                    fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
                  }}>
                    <span />
                    <span>Date</span><span>Job #</span><span>Broker · Route</span>
                    <span>Order # · Address</span>
                    <span style={{ textAlign: "right" }}>Hours</span>
                    <span style={{ textAlign: "right" }}>Miles</span>
                    <span style={{ textAlign: "right" }}>Guards</span>
                    <span>Type</span><span style={{ textAlign: "right" }}>Total</span>
                  </div>
                  {st.jobs.map((j) => {
                    const hasPaid = (j.invoices || []).some((i) => i.status === "paid");
                    return (
                    <div key={j.jobId || j.jobNumber || Math.random()} style={{
                      display: "grid", gridTemplateColumns: "24px 70px 105px 1fr 1.1fr 50px 50px 52px 70px 90px",
                      padding: "7px 10px", borderTop: "1px solid #f0f0f0",
                      fontSize: 11, alignItems: "center", gap: 8,
                    }}>
                      <input
                        type="checkbox"
                        checked={!!selHere?.has(jobKeyOf(j))}
                        disabled={hasPaid || moving}
                        onChange={() => toggleJobForMove(st, jobKeyOf(j))}
                        title={hasPaid
                          ? "This job has a paid invoice — unmark paid before moving it"
                          : "Select to move this job to another statement"}
                        style={{ cursor: hasPaid ? "not-allowed" : "pointer", margin: 0 }}
                      />
                      <div style={{ color: "#444" }}>
                        <div>{fmtJobDate(j)}</div>
                        {j.moved && (
                          <span
                            title={`On this statement rather than the one for its date (approved after that statement closed, or moved by hand) — the job actually ran ${fmtJobDate(j)}`}
                            style={{ fontSize: 9, fontWeight: 700, color: "#3C3489", background: "#EEEDFE", borderRadius: 8, padding: "1px 5px" }}
                          >later stmt</span>
                        )}
                      </div>
                      <span>
                        {j.jobId ? (
                          <button
                            type="button"
                            onClick={() => onViewJob(j.jobId)}
                            style={{
                              fontFamily: "monospace", fontSize: 11, color: "#185FA5",
                              background: "none", border: "none", padding: 0, cursor: "pointer",
                              textDecoration: "underline", textDecorationStyle: "dotted",
                            }}
                          >{j.jobNumber || "View job"}</button>
                        ) : (
                          <span style={{ fontFamily: "monospace" }}>{j.jobNumber || "—"}</span>
                        )}
                      </span>
                      {/* Broker + route, matching the Ready-to-Bill detail
                          the owner prefers. Route comes from the
                          denormalized invoice.routeSummary (blank on
                          pre-fix invoices → just shows the broker). */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: "#111", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {j.brokerName || "—"}
                        </div>
                        {j.routeSummary && (
                          <div style={{ fontSize: 10, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={j.routeSummary}>
                            {j.routeSummary}
                          </div>
                        )}
                      </div>
                      {/* Order/Receipt # + the package's address — one
                          line per package, denormalized via
                          invoice.packageDetails (backfilled for older
                          invoices on page load). */}
                      <div style={{ minWidth: 0 }}>
                        {(j.packageDetails || []).filter((p) => p.number || p.address || p.locationName).length > 0 ? (
                          j.packageDetails
                            .filter((p) => p.number || p.address || p.locationName)
                            .map((p, pi) => (
                              <div
                                key={pi}
                                style={{ fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                title={`${p.number || ""}${p.number ? " · " : ""}${p.address || p.locationName || ""}`}
                              >
                                {p.number && (
                                  <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#185FA5" }}>{p.number}</span>
                                )}
                                {p.number && (p.address || p.locationName) ? " · " : ""}
                                <span style={{ color: "#888" }}>{p.address || p.locationName || ""}</span>
                              </div>
                            ))
                        ) : (
                          <span style={{ fontSize: 10, color: "#ccc" }}>—</span>
                        )}
                      </div>
                      <span style={{ textAlign: "right", color: "#444" }}>
                        {j.hours ? j.hours.toFixed(2) : "—"}
                      </span>
                      <span style={{ textAlign: "right", color: "#444" }}>
                        {j.miles ? Math.round(j.miles) : "—"}
                      </span>
                      {/* Guards on this bill = one per-guard invoice per guard. */}
                      <span style={{ textAlign: "right", color: "#444" }} title="Guards billed on this job">
                        {j.invoices?.length || "—"}
                      </span>
                      <span style={{ fontSize: 10, textTransform: "uppercase", color: "#666" }}>{j.jobType || "—"}</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>{formatMoney(j.total)}</span>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
